import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Max width via Tailwind size classes (default: "max-w-2xl") */
  maxWidth?: string;
  /** If true, the title area is hidden from visible UI but still announced to SR */
  hideTitleVisually?: boolean;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Accessible modal dialog:
 * - Focus trap (first focusable element focused on open, Tab/Shift+Tab cycle)
 * - ESC closes
 * - Backdrop click closes
 * - Body scroll lock while open
 * - Restores focus to the trigger on close
 * - Rendered into document.body via portal
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  maxWidth = 'max-w-2xl',
  hideTitleVisually = false,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // Stable close handler — attached once to avoid resubscribing listeners
  const handleClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (!open) return;

    previousFocus.current = document.activeElement as HTMLElement | null;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const dialog = dialogRef.current;
    const focusables = dialog?.querySelectorAll<HTMLElement>(FOCUSABLE);
    focusables?.[0]?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
        return;
      }
      if (e.key === 'Tab' && dialog) {
        const list = dialog.querySelectorAll<HTMLElement>(FOCUSABLE);
        if (list.length === 0) return;
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', onKeyDown);
      previousFocus.current?.focus();
    };
  }, [open, handleClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          role="presentation"
          onMouseDown={(e) => {
            // Close only if the backdrop itself was clicked (not a child)
            if (e.target === e.currentTarget) handleClose();
          }}
        >
          <div className="absolute inset-0 bg-brand-purple/70 backdrop-blur-sm" aria-hidden="true" />
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className={cn(
              'relative w-full rounded-card-lg bg-white text-brand-purple shadow-2xl',
              'max-h-[90vh] flex flex-col',
              maxWidth
            )}
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            <div className="flex items-center justify-between gap-4 p-6 border-b border-brand-lightgray">
              <h2
                id={titleId}
                className={cn(
                  'text-xl font-semibold',
                  hideTitleVisually && 'sr-only'
                )}
              >
                {title}
              </h2>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Fechar"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full text-brand-purple hover:bg-brand-lightgray transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-yellow"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-6">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
